/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Skande BenHassine, ISW University of Stuttgart (for umati and VDW e.V.)
 */
import React, { ReactNode, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { getFair, getFairs } from '../../redux/selectors';
import { useSelector } from 'react-redux';
import { labels } from '@umati/dashboard-common';
import { IFair } from '@umati/dashboard-types';
import FrontendURLs from 'utilities/FrontendURLs';
import { IStore } from 'redux/storeType';

type ListType = 'machines' | 'overview' | 'software';

type IHeaderParams = {
  fair: string;
  selected: ListType;
}

interface IFairs {
  [key: string]: IFair
}

interface IHeaderProps {
  fairTag: string;
  detail?: {
    text: string;
    link: string;
    anchor: string;
  };
  listType?: ListType;
  overview?: boolean;
  containerClass: string;
  fixed?: boolean;
  machineId?: string;
}

const getListNavigationLink = (props: IHeaderProps, fair?: IFair, listType?: ListType) => {
  if (fair && listType) {
    return FrontendURLs.getList(listType, fair.Tag, props.detail?.anchor);
  }
  return undefined;
};

const getBreadCrumbNavigation = (props: IHeaderProps, fair?: IFair, listType?: ListType) => {
  const breadCrumbClassFair = 'breadcrumb-item';
  const breadCrumbClassMachine = 'breadcrumb-item active';
  const listLink = getListNavigationLink(props, fair, listType);
  const back = props.detail ? getListNavigationLink(props, fair, listType) : undefined;

  return (
    <div>
      <Link to={back || '/'} className="back">{labels.back}</Link>
      <nav className="breadcrumb-wrapper d-none d-lg-inline-flex">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Link to={'/'}>{labels.home}</Link>
          </li>
          {
            listLink &&
            <li className={breadCrumbClassFair}>
              <Link to={listLink}>{fair?.Name}</Link>
            </li>
          }
          {
            props.detail &&
            <li className={breadCrumbClassMachine}>
              <Link to={props.detail.link}>{props.detail.text}</Link>
            </li>
          }
          {
            props.machineId &&
            <li className="breadcrumb-item">
              <Link to={`/tvview/${fair?.Tag || 'all'}/2/${props.machineId}`}>TvView</Link>
            </li>
          }
        </ol>
      </nav>
    </div>
  );
};

const getDropDownFairList = (fairs: IFairs) => {
  const fairList: ReactNode[] = [];

  const CustomToggle = React.forwardRef<HTMLDivElement, { onClick: any, children: any }>(({ children, onClick }, ref) => (
    <div
      className="btn-map-select"
      ref={ref}
      onClick={(e) => {
        e.preventDefault();
        onClick(e);
      }}
    >
      {children}
    </div>
  ));
  CustomToggle.displayName = 'CustomToggle';

  const allMachines = fairs?.all;
  if (fairs) {
    for (const [fairKey, fair] of Object.entries(fairs)) {
      if (!fair.Hidden) fairList.push(<Dropdown.Item key={fairKey} href={FrontendURLs.getList('overview', fair.Tag)}>{fair.Name}</Dropdown.Item>);
    }
  }

  return (
    <Dropdown>
      <Dropdown.Toggle as={CustomToggle} id="dropdown-custom-components">
        <div className="btn-select-exhibition">{labels.selectExhibition}<span>({fairList.length} {labels.currentlyActiveExhibitions})</span></div>
      </Dropdown.Toggle>

      <Dropdown.Menu>
        <Dropdown.Item><b>{labels.exhibitions}</b></Dropdown.Item>
        {fairList}
        <div className="dropdown-divider"></div>
        {allMachines && <Dropdown.Item key={allMachines.Tag} href={FrontendURLs.getList('machines')}><b>{labels.machines}</b></Dropdown.Item>}
        <div className="dropdown-divider"></div>
        <Dropdown.Item key="sw" href={FrontendURLs.getList('software')}><b>{labels.software}</b></Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default function Header(props: IHeaderProps) {
  const isOverview = !!props.overview;
  const params = useParams<IHeaderParams>();
  const listType = props.listType || params.selected;
  const fairTag = props.fairTag || params.fair;
  const fair = useSelector((store: IStore) => getFair(store, fairTag));
  const fairs = useSelector((store: IStore) => getFairs(store));
  const dropdown = useMemo(() => getDropDownFairList(fairs), [fairs]);
  return (
    <div className={'header ' + (props.fixed ? 'header-fixed' : 'header-static')}>
      <div className={props.containerClass}>
        <div className="grid">
          <div className="logo-umati">
            <Link to={process.env.PUBLIC_URL || ''} className="logo" />
          </div>
          {fair && fairTag !== 'all' && <div className="logo-fair">
            <img src={fair.ImageFile} />
          </div>}
          <div className="breadcrumbs">
            {isOverview ? dropdown : getBreadCrumbNavigation(props, fair, listType)}
          </div>
          <div className="title">
            <span className="page-title">{props.overview ? labels.pageTitleOverview : labels.pageTitleElse}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
